import {
  CloseOutlined,
  PlusOutlined,
  RetweetOutlined,
} from "@ant-design/icons";
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Divider,
  Empty,
  Flex,
  Modal,
  Select,
  Skeleton,
  Space,
  Tag,
  Typography,
  message as antMessage,
} from "antd";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useApi from "../../utils/useApi";

const { Text, Title } = Typography;

// User-facing display format (app-wide standard).
const DATE_FORMAT = "DD-MM-YYYY";
// Whole-day helpers: we intentionally discard time-of-day for this screen.
// Starts at midnight (inclusive). Ends at 23:59:59 of the chosen day (inclusive
// last day), matching the existing overlap logic on the revenue backend.
const NAIVE_DATETIME_FORMAT = "YYYY-MM-DDTHH:mm:ss";
const toStartIso = (d) => dayjs(d).startOf("day").format(NAIVE_DATETIME_FORMAT);
const toEndIso = (d) => dayjs(d).endOf("day").format(NAIVE_DATETIME_FORMAT);

const formatDate = (iso) =>
  iso && dayjs(iso).isValid() ? dayjs(iso).format(DATE_FORMAT) : "-";

export default function ProductZonesEditorModal({
  open,
  product,
  onClose,
  onChanged,
}) {
  // useApi() returns fresh function references every render. Stash the api
  // object in a ref so effects/callbacks in this component don't retrigger
  // on every render — doing so caused an infinite fetch loop.
  const api = useApi();
  const apiRef = useRef(api);
  apiRef.current = api;

  const [messageApi, contextHolder] = antMessage.useMessage();

  const [zonesList, setZonesList] = useState([]); // [{value, label}]
  const [activeRelations, setActiveRelations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Per-relation edit state: { [zonecollection_id]: "end" | "replace" | null }
  const [rowAction, setRowAction] = useState({});
  const [endDateByRow, setEndDateByRow] = useState({});
  const [replaceZoneByRow, setReplaceZoneByRow] = useState({});
  const [replaceDateByRow, setReplaceDateByRow] = useState({});

  // Add-new-relation form
  const [showAddForm, setShowAddForm] = useState(false);
  const [newZoneId, setNewZoneId] = useState(null);
  const [newStartDate, setNewStartDate] = useState(dayjs());

  const legacyId = product?.legacy_id;
  const productLabel = product?.label || "";
  const isOuranCategory = product?.category_source === "ouran";
  const relationIdField = isOuranCategory
    ? "zone_ouran_category_id"
    : "zonecollection_id";

  const feedback = (type, text) => messageApi.open({ type, content: text });

  const resetForms = useCallback(() => {
    setRowAction({});
    setEndDateByRow({});
    setReplaceZoneByRow({});
    setReplaceDateByRow({});
    setShowAddForm(false);
    setNewZoneId(null);
    setNewStartDate(dayjs());
  }, []);

  const loadZones = useCallback(async () => {
    try {
      const [configsResponse, zonesResponse] = await Promise.all([
        apiRef.current.getZoneConfigs(),
        apiRef.current.getZones(),
      ]);
      const activeConfig = (configsResponse.data || []).find(
        (config) => config.status === true
      );
      if (!activeConfig) {
        setZonesList([]);
        return;
      }
      const active = (zonesResponse.data || [])
        .filter((zone) => zone.configuration === activeConfig.id)
        .map((zone) => ({ value: zone.value, label: zone.label }))
        .sort((a, b) =>
          String(a.label || "").localeCompare(String(b.label || ""), "pt-BR")
        );
      setZonesList(active);
    } catch (err) {
      console.error("Erro ao carregar zonas:", err);
      setZonesList([]);
    }
  }, []);

  const loadActiveRelations = useCallback(async () => {
    if (!legacyId) {
      setActiveRelations([]);
      return;
    }
    try {
      const rows = isOuranCategory
        ? await apiRef.current.getOuranZoneCategoryActiveZones(legacyId)
        : (await apiRef.current.getCollectionActiveZones(legacyId))?.data;
      setActiveRelations(Array.isArray(rows) ? rows : []);
    } catch (err) {
      console.error("Erro ao carregar vínculos ativos:", err);
      setActiveRelations([]);
    }
  }, [legacyId, isOuranCategory]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const run = async () => {
      setIsLoading(true);
      await Promise.all([loadZones(), loadActiveRelations()]);
      if (!cancelled) setIsLoading(false);
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [open, loadZones, loadActiveRelations]);

  useEffect(() => {
    if (!open) {
      resetForms();
    }
  }, [open, resetForms]);

  const linkedZoneIds = useMemo(
    () => new Set(activeRelations.map((r) => r.zone_id)),
    [activeRelations]
  );

  const zonesAvailableForNew = useMemo(
    () => zonesList.filter((z) => !linkedZoneIds.has(z.value)),
    [zonesList, linkedZoneIds]
  );

  // --- Actions ---

  const refreshAfterChange = async () => {
    await loadActiveRelations();
    if (typeof onChanged === "function") {
      onChanged();
    }
  };

  const handleEnd = async (relation) => {
    const relationId = relation[relationIdField];
    const endDate = endDateByRow[relationId] || dayjs();
    if (!endDate || !dayjs(endDate).isValid()) {
      feedback("error", "Selecione uma data válida para encerrar a relação.");
      return;
    }
    setIsSaving(true);
    try {
      if (isOuranCategory) {
        await apiRef.current.closeOuranZoneCategory(relationId, {
          union_end_datetime: toEndIso(endDate),
        });
      } else {
        await apiRef.current.patchZoneCollectionUnion(relation.zone_id, {
          union_end_datetime: toEndIso(endDate),
          collection: legacyId,
        });
      }
      feedback(
        "success",
        `Vínculo com a zona ${relation.zone_description} encerrado.`
      );
      await refreshAfterChange();
      setRowAction((prev) => ({
        ...prev,
        [relationId]: null,
      }));
    } catch (err) {
      console.error(err);
      feedback("error", "Não foi possível encerrar o vínculo.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleReplace = async (relation) => {
    const relationId = relation[relationIdField];
    const newZone = replaceZoneByRow[relationId];
    const switchDate = replaceDateByRow[relationId] || dayjs();

    if (!newZone) {
      feedback("error", "Selecione a nova zona.");
      return;
    }
    if (!switchDate || !dayjs(switchDate).isValid()) {
      feedback("error", "Selecione a data da troca.");
      return;
    }

    setIsSaving(true);
    try {
      // 1) End the current relation at end-of-day of the switch date.
      const endIso = toEndIso(switchDate);
      if (isOuranCategory) {
        await apiRef.current.closeOuranZoneCategory(relationId, {
          union_end_datetime: endIso,
        });
      } else {
        await apiRef.current.patchZoneCollectionUnion(relation.zone_id, {
          union_end_datetime: endIso,
          collection: legacyId,
        });
      }

      // 2) Ensure the collection exists in the app DB (no-op if already there).
      // 3) Start new relation on the next day at 00:00 to avoid overlap.
      const startIso = dayjs(switchDate)
        .add(1, "day")
        .startOf("day")
        .format(NAIVE_DATETIME_FORMAT);
      if (!isOuranCategory) {
        try {
          await apiRef.current.postCollection({
            description: productLabel,
            legacy_id: legacyId,
          });
        } catch (_) {
          // Collection likely already exists.
        }
      }
      if (isOuranCategory) {
        await apiRef.current.createOuranZoneCategory({
          zone_id: newZone,
          category_id: legacyId,
          union_start_datetime: startIso,
        });
      } else {
        await apiRef.current.postZonesCollections({
          zone: newZone,
          collection: legacyId,
          union_start_datetime: startIso,
        });
      }

      feedback("success", "Vínculo substituído com sucesso.");
      await refreshAfterChange();
      setRowAction((prev) => ({
        ...prev,
        [relationId]: null,
      }));
    } catch (err) {
      console.error(err);
      if (
        err?.response?.status === 400 &&
        err?.response?.data?.msg ===
          "This zone already has an active relation with the given collection."
      ) {
        feedback(
          "warning",
          "Esta zona já possui um vínculo ativo com o produto."
        );
      } else {
        feedback("error", "Não foi possível substituir o vínculo.");
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreate = async () => {
    if (!newZoneId) {
      feedback("error", "Selecione a zona para o novo vínculo.");
      return;
    }
    if (!newStartDate || !dayjs(newStartDate).isValid()) {
      feedback("error", "Selecione a data de início do vínculo.");
      return;
    }

    setIsSaving(true);
    try {
      if (!isOuranCategory) {
        try {
          await apiRef.current.postCollection({
            description: productLabel,
            legacy_id: legacyId,
          });
        } catch (_) {
          // Collection likely already exists.
        }
      }
      if (isOuranCategory) {
        await apiRef.current.createOuranZoneCategory({
          zone_id: newZoneId,
          category_id: legacyId,
          union_start_datetime: toStartIso(newStartDate),
        });
      } else {
        await apiRef.current.postZonesCollections({
          zone: newZoneId,
          collection: legacyId,
          union_start_datetime: toStartIso(newStartDate),
        });
      }

      feedback("success", "Novo vínculo criado com sucesso.");
      await refreshAfterChange();
      setShowAddForm(false);
      setNewZoneId(null);
      setNewStartDate(dayjs());
    } catch (err) {
      console.error(err);
      if (
        err?.response?.status === 400 &&
        err?.response?.data?.msg ===
          "This zone already has an active relation with the given collection."
      ) {
        feedback(
          "warning",
          "Esta zona já possui um vínculo ativo com o produto."
        );
      } else {
        feedback("error", "Não foi possível criar o vínculo.");
      }
    } finally {
      setIsSaving(false);
    }
  };

  // --- Render ---

  const renderRelationCard = (relation) => {
    const relationId = relation[relationIdField];
    const action = rowAction[relationId] || null;
    const currentEndDate = endDateByRow[relationId] || dayjs();
    const currentReplaceZone = replaceZoneByRow[relationId] || null;
    const currentReplaceDate = replaceDateByRow[relationId] || dayjs();

    const replaceOptions = zonesList.filter(
      (z) => z.value !== relation.zone_id && !linkedZoneIds.has(z.value)
    );

    return (
      <Card key={relationId} size="small" style={{ marginBottom: 12 }}>
        <Flex justify="space-between" align="flex-start" wrap="wrap" gap={8}>
          <div>
            <Tag color="blue">{relation.zone_description}</Tag>
            <Text type="secondary">
              Início: {formatDate(relation.union_start_datetime)}
            </Text>
          </div>
          <Space>
            <Button
              size="small"
              icon={<RetweetOutlined />}
              onClick={() =>
                setRowAction((prev) => ({
                  ...prev,
                  [relationId]: action === "replace" ? null : "replace",
                }))
              }>
              Alterar zona
            </Button>
            <Button
              size="small"
              danger
              icon={<CloseOutlined />}
              onClick={() =>
                setRowAction((prev) => ({
                  ...prev,
                  [relationId]: action === "end" ? null : "end",
                }))
              }>
              Encerrar
            </Button>
          </Space>
        </Flex>

        {action === "end" && (
          <>
            <Divider style={{ margin: "12px 0" }} />
            <Flex gap={12} align="center" wrap="wrap">
              <Text>Último dia do vínculo:</Text>
              <DatePicker
                format={DATE_FORMAT}
                value={currentEndDate}
                allowClear={false}
                onChange={(value) =>
                  setEndDateByRow((prev) => ({
                    ...prev,
                    [relationId]: value,
                  }))
                }
              />
              <Button
                type="primary"
                danger
                loading={isSaving}
                onClick={() => handleEnd(relation)}>
                Confirmar encerramento
              </Button>
            </Flex>
          </>
        )}

        {action === "replace" && (
          <>
            <Divider style={{ margin: "12px 0" }} />
            <Flex gap={12} align="center" wrap="wrap">
              <Text>Nova zona:</Text>
              <Select
                style={{ minWidth: 220 }}
                placeholder="Selecione a nova zona"
                value={currentReplaceZone}
                options={replaceOptions}
                onChange={(value) =>
                  setReplaceZoneByRow((prev) => ({
                    ...prev,
                    [relationId]: value,
                  }))
                }
              />
              <Text>Último dia na zona atual:</Text>
              <DatePicker
                format={DATE_FORMAT}
                value={currentReplaceDate}
                allowClear={false}
                onChange={(value) =>
                  setReplaceDateByRow((prev) => ({
                    ...prev,
                    [relationId]: value,
                  }))
                }
              />
              <Button
                type="primary"
                loading={isSaving}
                onClick={() => handleReplace(relation)}>
                Confirmar troca
              </Button>
            </Flex>
            <Text type="secondary" style={{ fontSize: 12 }}>
              O novo vínculo iniciará no dia seguinte ao último dia informado.
            </Text>
          </>
        )}
      </Card>
    );
  };

  return (
    <Modal
      open={open}
      title={
        <div>
          <Title level={5} style={{ margin: 0 }}>
            Editar vínculos de zona
          </Title>
          <Text type="secondary">{productLabel}</Text>
        </div>
      }
      onCancel={onClose}
      footer={
        <Button onClick={onClose} disabled={isSaving}>
          Fechar
        </Button>
      }
      width={720}
      destroyOnClose>
      {contextHolder}
      {isLoading ? (
        <Skeleton active />
      ) : (
        <Flex vertical gap={16}>
          <div>
            <Title level={5} style={{ marginBottom: 8 }}>
              Vínculos ativos
            </Title>
            {activeRelations.length === 0 ? (
              <Empty description="O produto não está vinculado a nenhuma zona no momento." />
            ) : (
              activeRelations.map(renderRelationCard)
            )}
          </div>

          <Divider style={{ margin: "0" }} />

          <div>
            <Flex justify="space-between" align="center">
              <Title level={5} style={{ margin: 0 }}>
                Criar novo vínculo
              </Title>
              {!showAddForm && (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setShowAddForm(true)}
                  disabled={zonesAvailableForNew.length === 0}>
                  Adicionar zona
                </Button>
              )}
            </Flex>

            {showAddForm && (
              <Card size="small" style={{ marginTop: 12 }}>
                {zonesAvailableForNew.length === 0 ? (
                  <Alert
                    type="info"
                    showIcon
                    message="Todas as zonas disponíveis já estão vinculadas a este produto."
                  />
                ) : (
                  <Flex gap={12} align="center" wrap="wrap">
                    <Text>Zona:</Text>
                    <Select
                      style={{ minWidth: 220 }}
                      placeholder="Selecione a zona"
                      value={newZoneId}
                      options={zonesAvailableForNew}
                      onChange={setNewZoneId}
                    />
                    <Text>Início:</Text>
                    <DatePicker
                      format={DATE_FORMAT}
                      value={newStartDate}
                      allowClear={false}
                      onChange={(value) => setNewStartDate(value)}
                    />
                    <Space>
                      <Button
                        type="primary"
                        loading={isSaving}
                        onClick={handleCreate}>
                        Confirmar
                      </Button>
                      <Button
                        onClick={() => {
                          setShowAddForm(false);
                          setNewZoneId(null);
                          setNewStartDate(dayjs());
                        }}
                        disabled={isSaving}>
                        Cancelar
                      </Button>
                    </Space>
                  </Flex>
                )}
              </Card>
            )}
          </div>
        </Flex>
      )}
    </Modal>
  );
}
